"use client";

import React, { useState } from "react";
import { AlertTriangle, ShieldAlert, Calendar, Info, MessageSquare } from "lucide-react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";

interface Application {
    id: string;
    gig_posting_id: string;
    gig_posting_title: string;
    owning_entity_type: string;
    owning_entity_id: number;
    owning_entity_name?: string;
    volunteer_id: string;
    volunteer_name: string;
    volunteer_email: string;
    status: string;
    applied_at: string | null;
    note: string | null;
    geo_flag_confirmed: boolean;
    geo_mismatch_detected: boolean;
}

interface ConsoleInterestQueueProps {
    applications: Application[];
    isLoading: boolean;
    onSelectVolunteer?: (id: string) => void;
    onActionSuccess?: () => void;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function getStatusStyle(status: string): string {
    const s = status.toLowerCase();
    if (s === "submitted")         return "bg-amber-50 text-amber-700 border-amber-100";
    if (s === "approved")          return "bg-[#cbeef2]/30 text-[#007a8a] border-[#007a8a]/20";
    if (s === "rejected")          return "bg-red-50 text-red-700 border-red-100";
    if (s === "in_candidate_pool") return "bg-[#a9c4bb]/20 text-[#004d57] border-[#a9c4bb]/40";
    return "bg-gray-50 text-gray-600 border-gray-200";
}

function getEntityStyle(type: string): string {
    const t = type.toLowerCase();
    if (t === "staff")     return "bg-rose-50 text-rose-700 border-rose-100";
    if (t === "committee") return "bg-[#003d45]/5 text-[#003d45] border-[#003d45]/10";
    if (t === "chapter")   return "bg-[#007a8a]/5 text-[#007a8a] border-[#007a8a]/10";
    if (t === "community") return "bg-[#ae232d]/5 text-[#ae232d] border-[#ae232d]/10";
    return "bg-[#64748b]/5 text-[#64748b] border-[#64748b]/10";
}

function initials(name: string): string {
    const parts = name.trim().split(" ");
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : (name[0] || "V").toUpperCase();
}

// ── component ────────────────────────────────────────────────────────────────

export default function ConsoleInterestQueue({
    applications,
    isLoading,
    onSelectVolunteer,
    onActionSuccess,
}: ConsoleInterestQueueProps) {
    const [actioningId, setActioningId]         = useState<string | null>(null);
    const [rejectingId, setRejectingId]         = useState<string | null>(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const [approvingId, setApprovingId]         = useState<string | null>(null);
    const [approvalMessage, setApprovalMessage] = useState("");
    const [actionError, setActionError]         = useState<string | null>(null);

    const handleTriage = async (id: string, approve: boolean, message?: string) => {
        if (!approve && !rejectionReason.trim()) {
            setActionError("Please provide a rejection reason."); return;
        }
        setActioningId(id); setActionError(null);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/console/applications/${id}/triage`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    approve,
                    rejection_reason: approve ? null : rejectionReason.trim(),
                    approval_message: approve ? message?.trim() || null : null
                }),
            });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Failed to triage application."); }
            setRejectingId(null); setRejectionReason("");
            setApprovingId(null); setApprovalMessage("");
            if (onActionSuccess) onActionSuccess();
        } catch (err: any) { setActionError(err.message || "Failed to triage application."); }
        finally { setActioningId(null); }
    };

    const formatDate = (dStr: string | null) => {
        if (!dStr) return "N/A";
        return new Date(dStr).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    };

    // ── loading / empty ───────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div className="py-12 flex flex-col items-center justify-center gap-3">
                <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Loading applications...</p>
            </div>
        );
    }

    if (applications.length === 0) {
        return (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-150 shadow-sm">
                <ShieldAlert className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-[#003d45] uppercase tracking-wider">Queue is Empty</h3>
                <p className="text-xs text-gray-400 mt-1">There are no pending interest applications in this scope.</p>
            </div>
        );
    }

    const showActions = !!onSelectVolunteer || !!onActionSuccess;

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-4">
            {actionError && (
                <div className="bg-red-50 text-red-700 p-4 border border-red-100 rounded-2xl text-xs flex items-center gap-2">
                    <Info className="w-4 h-4 text-red-500 shrink-0" />
                    <span><strong>Error:</strong> {actionError}</span>
                </div>
            )}

            <div className="flex flex-col gap-4">
                {applications.map((a) => {
                    const isPending  = a.status.toLowerCase() === "submitted";
                    const isGeoMismatch = a.geo_mismatch_detected;
                    const isGeoConfirmed = a.geo_flag_confirmed;

                    return (
                        <div
                            key={a.id}
                            className={`bg-white rounded-xl border border-gray-150 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row md:items-center justify-between gap-6 text-left ${
                                isGeoMismatch ? "border-l-4 border-l-amber-400" : ""
                            }`}
                        >
                            {/* Column 1: Volunteer + gig details */}
                            <div className="space-y-1.5 flex-1 min-w-0">
                                {/* Volunteer name */}
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <div className="w-7 h-7 rounded-full bg-[#003d45] text-white text-[10px] font-black flex items-center justify-center uppercase shrink-0">
                                        {initials(a.volunteer_name)}
                                    </div>
                                    <button
                                        onClick={() => onSelectVolunteer && onSelectVolunteer(a.volunteer_id)}
                                        className={`text-base font-bold text-[#003d45] text-left focus:outline-none transition-colors leading-tight ${onSelectVolunteer ? "hover:text-[#007a8a] cursor-pointer" : "cursor-default"}`}
                                    >
                                        {a.volunteer_name}
                                    </button>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${getStatusStyle(a.status)}`}>
                                        {a.status.replace(/_/g, " ")}
                                    </span>
                                    {isGeoMismatch && (
                                        <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider">
                                            <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                                            Location mismatch {isGeoConfirmed ? "(Acknowledged)" : ""}
                                        </span>
                                    )}
                                </div>

                                {/* Gig + entity */}
                                <p className="text-xs text-gray-400 font-medium flex items-center flex-wrap gap-x-1.5">
                                    <span className="text-gray-500 font-semibold">{a.gig_posting_title}</span>
                                    <span className="text-gray-300">·</span>
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${getEntityStyle(a.owning_entity_type)}`}>
                                        {a.owning_entity_name || `${a.owning_entity_type}: ${a.owning_entity_id}`}
                                    </span>
                                </p>

                                {/* Application note */}
                                {a.note && (
                                    <div className="flex items-start gap-2 pt-0.5">
                                        <MessageSquare className="w-3 h-3 text-gray-300 shrink-0 mt-0.5" />
                                        <p className="text-[11px] text-gray-500 leading-relaxed italic line-clamp-2">
                                            "{a.note}"
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Column 2: Date */}
                            <div className="text-center shrink-0 w-24">
                                <div className="flex items-center justify-center gap-1 text-gray-400 mb-0.5">
                                    <Calendar className="w-3.5 h-3.5" />
                                </div>
                                <div className="text-[10px] font-bold text-gray-500 text-center">
                                    {a.applied_at
                                        ? new Date(a.applied_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                        : "—"}
                                </div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mt-0.5">Applied</div>
                            </div>

                            {/* Column 3: Actions */}
                            {showActions && (
                                <div className="flex items-center gap-3 shrink-0 self-end md:self-center">
                                    {isPending && onActionSuccess ? (
                                        approvingId === a.id ? (
                                            <div className="space-y-2 text-left w-48">
                                                <input
                                                    type="text"
                                                    placeholder="Comments / info (optional)..."
                                                    value={approvalMessage}
                                                    onChange={(e) => setApprovalMessage(e.target.value)}
                                                    className="w-full border border-gray-250 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-emerald-500 font-medium bg-white"
                                                    disabled={actioningId === a.id}
                                                />
                                                <div className="flex gap-2 justify-end">
                                                    <button
                                                        onClick={() => { setApprovingId(null); setApprovalMessage(""); }}
                                                        className="px-3 py-1.5 rounded border border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:bg-gray-50 cursor-pointer bg-white"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={() => handleTriage(a.id, true, approvalMessage)}
                                                        className="px-3 py-1.5 bg-[#004d57] hover:bg-[#003c44] rounded text-[10px] font-bold uppercase tracking-wider text-white cursor-pointer border-none"
                                                        disabled={actioningId === a.id}
                                                    >
                                                        Submit
                                                    </button>
                                                </div>
                                            </div>
                                        ) : rejectingId === a.id ? (
                                            <div className="space-y-2 text-left w-48">
                                                <input
                                                    type="text"
                                                    placeholder="Rejection reason..."
                                                    value={rejectionReason}
                                                    onChange={(e) => setRejectionReason(e.target.value)}
                                                    className="w-full border border-gray-250 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-red-500 font-medium bg-white"
                                                    disabled={actioningId === a.id}
                                                />
                                                <div className="flex gap-2 justify-end">
                                                    <button
                                                        onClick={() => { setRejectingId(null); setRejectionReason(""); }}
                                                        className="px-3 py-1.5 rounded border border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:bg-gray-50 cursor-pointer bg-white"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={() => handleTriage(a.id, false)}
                                                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-[10px] font-bold uppercase tracking-wider text-white cursor-pointer border-none"
                                                        disabled={actioningId === a.id}
                                                    >
                                                        Submit
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => { setApprovingId(a.id); setRejectingId(null); setApprovalMessage(""); }}
                                                    className="bg-[#004d57] text-white rounded-lg px-4 py-2 font-bold text-xs uppercase tracking-wider hover:bg-[#003c44] transition-all border-none cursor-pointer shadow-sm shadow-[#004d57]/10"
                                                    disabled={actioningId !== null}
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => { setRejectingId(a.id); setApprovingId(null); setRejectionReason(""); }}
                                                    className="text-rose-600 hover:text-rose-800 font-semibold text-[11.5px] cursor-pointer border-none bg-transparent hover:underline"
                                                    disabled={actioningId !== null}
                                                >
                                                    Reject
                                                </button>
                                            </>
                                        )
                                    ) : null}
                                    {onSelectVolunteer && (
                                        <button
                                            onClick={() => onSelectVolunteer(a.volunteer_id)}
                                            className="text-[#004d57] hover:text-[#003c44] font-semibold text-[11px] cursor-pointer border-none bg-transparent hover:underline uppercase tracking-wider"
                                            disabled={actioningId !== null}
                                        >
                                            Profile
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
